import { z } from 'zod';
import {
  prefixed,
  zAchievementId,
  zAvatarFrameId,
  zAvatarPartId,
  zBadgeId,
  zBoxDefId,
  zChapterId,
  zDayKey,
  zExpansionId,
  zFeatureKey,
  zFlagId,
  zItemId,
  zLetterId,
  zMiniGameId,
  zNpcId,
  zPhotoFrameId,
  zPostcardId,
  zProductId,
  zRoomId,
  zRotation,
  zSceneId,
  zStickerId,
  zTitleId,
  zUnlockId,
  zWallFloorId,
  zWallSide,
} from '@/core/content/schemas/ids';
import { zReward } from '@/core/content/schemas/progression';
import { zNeighborhoodStoryId } from '@/core/content/schemas/neighborhood';

/** GameSave v1 (docs/tech/content-schemas.md §10). Pre-release changes stay in v1; reset dev DBs instead. */
export const SAVE_SCHEMA_VERSION = 1 as const;

const zInt0 = z.number().int().min(0);
const zMs = z.number().min(0);

export const zInstanceId = z.custom<`i_${string}`>((v) => typeof v === 'string' && /^i_[a-z0-9]+$/.test(v), {
  message: 'InstanceId must look like i_<base36>',
});
export const zBoxInstanceId = prefixed('bx_');
export const zSlotKey = z.custom<`i_${string}#${number}`>(
  (v) => typeof v === 'string' && /^i_[a-z0-9]+#\d+$/.test(v),
  { message: 'SlotKey must look like i_<id>#<index>' },
);

// ---------- Inventory ----------
export const zItemInstance = z.object({
  instanceId: zInstanceId,
  itemId: zItemId.refine((id) => !id.startsWith('prod_'), 'products are fungible and have no instances'),
  acquiredBy: z.enum(['starter', 'purchase', 'gift', 'story', 'reward', 'mail', 'preplaced', 'dialogue']),
  acquiredAtPlayMs: zMs,
  location: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('bag') }),
    z.object({ kind: z.literal('placed'), roomId: zRoomId }),
  ]),
});
export type ItemInstance = z.output<typeof zItemInstance>;

export const zBoxInstance = z.object({
  boxInstanceId: zBoxInstanceId,
  boxDefId: zBoxDefId,
  roomId: zRoomId,
  x: z.number().int(),
  y: z.number().int(),
  opened: z.boolean(),
});
export type BoxInstance = z.output<typeof zBoxInstance>;

export const zInventory = z.object({
  instances: z.record(zInstanceId, zItemInstance),
  productStacks: z.record(zProductId, zInt0),
  boxes: z.record(zBoxInstanceId, zBoxInstance),
  nextInstanceSeq: zInt0,
  favorites: z.array(zItemId),
  orphans: z.array(z.object({ instanceId: z.string(), itemId: z.string() })),
});
export type InventoryState = z.output<typeof zInventory>;

// ---------- Rooms ----------
export const zFloorPlacement = z.object({
  instanceId: zInstanceId,
  layer: z.enum(['floor', 'object']),
  x: z.number().int(),
  y: z.number().int(),
  rotation: zRotation,
});
export const zWallPlacement = z.object({
  instanceId: zInstanceId,
  layer: z.literal('wall'),
  wall: zWallSide,
  offset: zInt0,
});
export const zPlacement = z.union([zFloorPlacement, zWallPlacement]);
export type FloorPlacement = z.output<typeof zFloorPlacement>;
export type WallPlacement = z.output<typeof zWallPlacement>;
export type Placement = z.output<typeof zPlacement>;

export const zSlotContent = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('instance'), instanceId: zInstanceId }),
  z.object({ kind: z.literal('product'), itemId: zProductId }),
]);
export type SlotContent = z.output<typeof zSlotContent>;

export const zRoomState = z.object({
  roomId: zRoomId,
  wallId: zWallFloorId,
  floorId: zWallFloorId,
  expansions: z.array(zExpansionId),
  placements: z.record(zInstanceId, zPlacement),
  slots: z.record(zSlotKey, zSlotContent),
});
export type RoomState = z.output<typeof zRoomState>;

/** Studio NANO before it opens (studio-nano.md §3); also what saves from before the studio parse to. */
export const EMPTY_STUDIO_ROOM: RoomState = {
  roomId: 'studio',
  wallId: 'wall_studio_paper',
  floorId: 'floor_studio_parquet',
  expansions: [],
  placements: {},
  slots: {},
};

// ---------- Shop & orders ----------
export const zShop = z.object({
  isOpen: z.boolean(),
  perks: z.array(z.enum(['faster_customers', 'regulars_buy_more'])),
  sim: z.object({
    nextCustomerAtPlayMs: zMs.nullable(),
    rngState: zInt0,
    customerSeq: zInt0,
    active: z.array(
      z.object({
        customerId: prefixed('c_'),
        visitorNpcId: zNpcId.optional(),
        phase: z.enum(['browsing', 'checkout']),
        basket: z.array(z.object({ itemId: zProductId, fromSlot: zSlotKey })),
        checkoutAtPlayMs: zMs,
      }),
    ),
  }),
  ledgerSummary: z.object({
    revenueTotal: zInt0,
    unitsSoldTotal: zInt0,
    salesCount: zInt0,
    stockSpentTotal: zInt0,
    catalogSpentTotal: zInt0,
    rewardsCoinsTotal: zInt0,
    byProduct: z.record(zProductId, z.object({ sold: zInt0, revenue: zInt0 })),
  }),
});

export const zOrders = z.object({
  open: z.array(
    z.object({
      orderId: prefixed('o_'),
      templateId: prefixed('ord_'),
      npcId: zNpcId,
      lines: z.array(z.object({ itemId: zProductId, qty: z.number().int().positive() })),
      rewardCoins: zInt0,
      friendship: zInt0,
      createdAtPlayMs: zMs,
    }),
  ),
  nextOrderAtPlayMs: zMs.nullable(),
  firstOrderDone: z.record(zNpcId, z.boolean()),
  orderSeq: zInt0,
  rngState: zInt0,
});

// ---------- Studio NANO (studio-nano.md §4) ----------
export const zShootGrade = z.enum(['cute', 'very_cute', 'excellent']);
export type ShootGrade = z.output<typeof zShootGrade>;

export const zStudio = z.object({
  /** Bookings waiting in the Sổ hẹn (at most balance.studio.maxOpenRequests, one per neighbour). */
  requests: z.array(
    z.object({
      requestId: prefixed('sr_'),
      shootId: prefixed('shoot_'),
      npcId: zNpcId,
      createdAtPlayMs: zMs,
    }),
  ),
  /** The shoot on the set: the client poses during `setup`, and stays for a souvenir photo once `done`. */
  session: z
    .object({
      requestId: prefixed('sr_'),
      shootId: prefixed('shoot_'),
      npcId: zNpcId,
      phase: z.enum(['setup', 'done']),
      grade: zShootGrade.optional(),
    })
    .nullable(),
  /** Tiếng lành points; the level is derived from balance.studio.levels. */
  reputation: zInt0,
  firstShootDone: z.record(zNpcId, z.boolean()),
  nextRequestAtPlayMs: zMs.nullable(),
  seq: zInt0,
  rngState: zInt0,
});
export type StudioState = z.output<typeof zStudio>;

export const EMPTY_STUDIO: StudioState = {
  requests: [],
  session: null,
  reputation: 0,
  firstShootDone: {},
  nextRequestAtPlayMs: null,
  seq: 0,
  rngState: 0,
};

// ---------- Progression ----------
export const zCoziness = z.object({ current: zInt0, best: zInt0, bestLevel: zInt0 });

export const zNpcState = z.object({
  friendship: zInt0,
  level: zInt0,
  talkCount: zInt0,
  lastDialogueId: prefixed('dlg_').nullable(),
  pendingVisits: z.array(zRoomId),
  activeVisits: z.array(zRoomId),
});

export const zDaily = z.object({
  dayKey: zDayKey,
  perNpc: z.record(
    zNpcId,
    z.object({
      talkBonusGiven: z.boolean(),
      favoriteBuyPoints: zInt0,
      petPoints: zInt0,
      pateBonusGiven: z.boolean(),
    }),
  ),
});

export const zMailItem = z.object({
  mailId: z.string().regex(/^mail_(\d+|special_[a-z0-9_]+)$/),
  grantedForDayKey: zDayKey,
  cycleDay: z.number().int().min(0).max(7),
  rewards: z.array(zReward),
  kindForUi: z.enum(['postcard', 'coins', 'miniBox', 'npcLetter', 'sticker', 'postcardAndBox', 'special']),
});

export const zMailbox = z.object({
  lastGrantedDayKey: zDayKey.nullable(),
  grantsCount: zInt0,
  postcardCursor: zInt0,
  letterCursor: zInt0,
  stickerCursor: zInt0,
  miniBoxCursor: zInt0,
  pending: z.array(zMailItem),
  special: z.array(zMailItem),
  claimedCount: zInt0,
});

export const zMiniGameState = z.object({
  levelsCleared: z.array(z.number().int().min(1)),
  firstClearAtPlayMs: z.record(z.string(), zMs),
  bestScore: z.record(z.string(), z.number().min(0).max(1)),
  plays: zInt0,
  lastRewardAtPlayMs: zMs.nullable(),
  recentRunIds: z.array(prefixed('run_')),
  /** Last `repeatBonus` grant (Bếp của bà: a homemade dish). Older saves: never. */
  lastBonusAtPlayMs: zMs.nullable().default(null),
  /** Result-card notes met so far (Sổ công thức của bà). Older saves: none. */
  seenNoteKeys: z.array(z.string()).default([]),
  /** Actions whose first-time hand hint was shown (Bếp của bà §4.9). Older saves: none. */
  seenActions: z.array(z.string()).default([]),
});

export const zStory = z.object({
  unlockedChapters: z.array(zChapterId),
  seenChapters: z.array(zChapterId),
  postcards: z.array(z.object({ postcardId: zPostcardId, receivedCount: z.number().int().positive() })),
  letters: z.array(zLetterId),
  // Additive update: saves written before neighbourhood stories start with no quests.
  neighborhood: z
    .partialRecord(
      zNeighborhoodStoryId,
      z.object({
        stage: z.number().int().min(0).max(4),
        salesBaseline: zInt0,
      }),
    )
    .default({}),
});

/** Longest player name shown in the Menu and S20 (characters, after trimming). */
export const AVATAR_NAME_MAX = 16;

export const zAvatar = z.object({
  hair: zAvatarPartId,
  shirtColor: zAvatarPartId,
  hat: zAvatarPartId,
  frame: zAvatarFrameId.nullable(),
  // Optional, so saves from before S20 names stay valid without a migration (absent = "Bạn").
  name: z
    .string()
    .max(AVATAR_NAME_MAX * 2)
    .optional(),
});

export type AvatarState = z.output<typeof zAvatar>;

export const zCosmetics = z.object({
  titles: z.array(zTitleId),
  badges: z.array(zBadgeId),
  stickers: z.array(zStickerId),
  photoFrames: z.array(zPhotoFrameId),
  avatarParts: z.array(zAvatarPartId),
  avatarFrames: z.array(zAvatarFrameId),
  equippedTitle: zTitleId.nullable(),
});

export const zAchievementState = z.object({
  completedAtPlayMs: zMs.nullable(),
  claimedAtPlayMs: zMs.nullable(),
  seen: z.boolean(),
});

export const zStats = z.object({
  counters: z.record(z.string(), zInt0),
  distinct: z.record(z.string(), z.array(z.string())),
});
export type StatsState = z.output<typeof zStats>;

export const zTaskRuntime = z.object({
  status: z.enum(['locked', 'available', 'active', 'completed', 'claimed']),
  activatedAtPlayMs: zMs.optional(),
  baseline: zInt0.optional(),
  eventCount: zInt0.optional(),
  distinctSeen: z.array(z.string()).optional(),
});

export const zSuggestions = z.object({
  tasks: z.record(z.string().regex(/^[TO]\d{2}$/), zTaskRuntime),
  optionalActive: z.array(z.string().regex(/^O\d{2}$/)).max(3),
  repeatable: z.object({
    active: z.array(
      z.object({
        instanceKey: z.string().regex(/^rep#\d+$/),
        taskId: prefixed('rep_'),
        runtime: zTaskRuntime,
      }),
    ),
    seq: zInt0,
    lastClaimAtPlayMs: zMs.nullable(),
    rngState: zInt0,
  }),
});

// ---------- Session & settings ----------
export const zPanelId = z.enum([
  'none',
  'bag',
  'catalog',
  'tasks',
  'achievements',
  'mailbox',
  'orders',
  'album',
  'settings',
  'credits',
  'unlocks',
  'npc',
]);

export const zCameraState = z.object({ x: z.number(), y: z.number(), zoom: z.number().positive() });
export type CameraState = z.output<typeof zCameraState>;

export const zLastSession = z.object({
  sceneId: zSceneId,
  cameras: z.object({
    home: zCameraState.optional(),
    minimart: zCameraState.optional(),
    studio: zCameraState.optional(),
    townmap: zCameraState.optional(),
  }),
  panel: zPanelId,
  panelParams: z.record(z.string(), z.string()).optional(),
  savedAtWallMs: zMs,
});
export type LastSession = z.output<typeof zLastSession>;

export const zSettings = z.object({
  locale: z.enum(['vi', 'en']),
  muted: z.boolean(),
  musicEnabled: z.boolean(),
  /** Game music or the player's own tracks (kept in IndexedDB on this device only). Older saves: the game's. */
  musicSource: z.enum(['game', 'device']).default('game'),
  musicVolume: z.number().min(0).max(1),
  sfxVolume: z.number().min(0).max(1),
  haptics: z.boolean(),
  reducedMotion: z.enum(['system', 'on', 'off']),
  textScale: z.union([z.literal(1), z.literal(1.15), z.literal(1.3)]),
  quality: z.enum(['auto', 'low', 'medium', 'high']),
  colorAssist: z.boolean(),
  achievementToasts: z.boolean(),
  bossButtonEnabled: z.boolean(),
  bossHotkey: z.string().min(1),
  photoWatermark: z.boolean(),
  /** Accessibility: tap to start and tap again to stop instead of press-and-hold (minigames.md §4.4). */
  tapToHold: z.boolean().default(false),
});
export type SettingsState = z.output<typeof zSettings>;

export const DEFAULT_SETTINGS: SettingsState = {
  locale: 'vi',
  muted: false,
  musicEnabled: false,
  musicSource: 'game',
  musicVolume: 0.6,
  sfxVolume: 0.5,
  haptics: true,
  reducedMotion: 'system',
  textScale: 1,
  quality: 'auto',
  colorAssist: false,
  achievementToasts: true,
  bossButtonEnabled: false,
  bossHotkey: 'Backquote',
  photoWatermark: true,
  tapToHold: false,
};

// ---------- Root ----------
const zGameSaveShape = z.object({
  schemaVersion: z.literal(SAVE_SCHEMA_VERSION),
  saveId: z.string().min(8),
  revision: zInt0,
  createdAt: zMs,
  updatedAt: zMs,
  contentVersion: z.string(),
  rngSeed: z.number().int().min(0).max(0xffffffff),
  clock: z.object({ playTimeMs: zMs, firstDayKey: zDayKey, lastSeenDayKey: zDayKey }),
  wallet: z.object({ coins: zInt0 }),
  stars: z.object({ total: zInt0, sources: z.record(z.string(), z.number().int().positive()) }),
  inventory: zInventory,
  // Additive update: saves from before Studio NANO get an empty, still-locked studio.
  rooms: z.object({ home: zRoomState, minimart: zRoomState, studio: zRoomState.default(EMPTY_STUDIO_ROOM) }),
  ownedWallFloors: z.array(zWallFloorId),
  /** Catalog entries seen since they unlocked ("Mới" badge, ux-screens §3.6). Older saves default to none. */
  catalogSeen: z.array(z.string()).default([]),
  shop: zShop,
  orders: zOrders,
  coziness: z.object({
    home: zCoziness,
    minimart: zCoziness,
    studio: zCoziness.default({ current: 0, best: 0, bestLevel: 0 }),
  }),
  studio: zStudio.default(EMPTY_STUDIO),
  npcs: z.record(zNpcId, zNpcState),
  daily: zDaily,
  mailbox: zMailbox,
  miniGames: z.record(zMiniGameId, zMiniGameState),
  story: zStory,
  album: z.object({ photoIds: z.array(prefixed('ph_')) }),
  avatar: zAvatar,
  cosmetics: zCosmetics,
  achievements: z.record(zAchievementId, zAchievementState),
  stats: zStats,
  suggestions: zSuggestions,
  unlockedIds: z.array(zUnlockId),
  features: z.array(zFeatureKey),
  scenes: z.array(zSceneId),
  flags: z.record(zFlagId, z.boolean()),
  dialogue: z.object({ appliedEffectKeys: z.array(z.string()), seenDialogueIds: z.array(prefixed('dlg_')) }),
  rewardKeys: z.array(z.string()),
  txLog: z.object({ recent: z.array(z.object({ txId: z.string(), atPlayMs: zMs })) }),
  lastSession: zLastSession,
  settings: zSettings,
});

export type GameSave = z.output<typeof zGameSaveShape>;

/**
 * Structural invariants that do not need content (content-schemas §11.4 items 1, 2, 3, 7).
 * Placement geometry (item 4) needs the content registry and runs in reconcile.
 */
export function checkSaveInvariants(save: GameSave): string[] {
  const issues: string[] = [];
  const starSum = Object.values(save.stars.sources).reduce((a, b) => a + b, 0);
  if (starSum !== save.stars.total) issues.push(`stars.total ${save.stars.total} ≠ Σ sources ${starSum}`);

  const seen = new Map<string, string>();
  const claim = (instanceId: string, where: string) => {
    const prev = seen.get(instanceId);
    if (prev) issues.push(`${instanceId} is in both ${prev} and ${where}`);
    seen.set(instanceId, where);
  };

  for (const roomId of ['home', 'minimart', 'studio'] as const) {
    const room = save.rooms[roomId];
    if (room.roomId !== roomId) issues.push(`rooms.${roomId}.roomId mismatch`);
    for (const [key, placement] of Object.entries(room.placements)) {
      if (key !== placement.instanceId) issues.push(`placement key ${key} ≠ ${placement.instanceId}`);
      claim(placement.instanceId, `${roomId}.placements`);
      const inst = save.inventory.instances[placement.instanceId];
      if (inst?.location.kind !== 'placed' || inst.location.roomId !== roomId) {
        issues.push(`${placement.instanceId} placed in ${roomId} but its location disagrees`);
      }
    }
    for (const [slotKey, content] of Object.entries(room.slots)) {
      const parentId = slotKey.slice(0, slotKey.indexOf('#'));
      if (!(parentId in room.placements)) issues.push(`slot ${slotKey} has no placed parent`);
      if (content.kind === 'instance') {
        claim(content.instanceId, `${roomId}.slots`);
        const inst = save.inventory.instances[content.instanceId];
        if (inst?.location.kind !== 'placed' || inst.location.roomId !== roomId) {
          issues.push(`${content.instanceId} in slot ${slotKey} but its location disagrees`);
        }
      }
    }
  }
  for (const inst of Object.values(save.inventory.instances)) {
    if (inst.location.kind === 'placed' && !seen.has(inst.instanceId)) {
      issues.push(`${inst.instanceId} says placed but is not in any room`);
    }
  }
  for (const [id, a] of Object.entries(save.achievements)) {
    if (a.claimedAtPlayMs !== null && a.completedAtPlayMs === null)
      issues.push(`${id} claimed before completed`);
  }
  if (new Set(save.unlockedIds).size !== save.unlockedIds.length) issues.push('duplicate unlockedIds');
  return issues;
}

export const zGameSaveV1 = zGameSaveShape.superRefine((save, ctx) => {
  for (const message of checkSaveInvariants(save)) ctx.addIssue({ code: 'custom', message });
});
